// CRM-NANCOZI — Page de réinitialisation de mot de passe
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Loader, CheckCircle, ArrowLeft } from 'lucide-react';
import { demanderResetPassword, confirmerResetPassword } from '../store/slices/authSlice';
import bgImage from '../img/2.jpg';
import logo from '../img/logo1.png';

// ── Calcul force mot de passe ──────────────────────────────────────────────
function forceMotDePasse(pwd) {
  if (!pwd || pwd.length < 6) return { niveau: 'Faible', couleur: '#DC2626', pct: 25 };
  const score = [
    pwd.length >= 10,
    /[A-Z]/.test(pwd),
    /\d/.test(pwd),
    /[!@#$%^&*]/.test(pwd),
  ].filter(Boolean).length;
  if (score <= 1) return { niveau: 'Faible',  couleur: '#DC2626', pct: 30 };
  if (score === 2) return { niveau: 'Moyen',   couleur: '#F59E0B', pct: 60 };
  if (score === 3) return { niveau: 'Bien',    couleur: '#3B82F6', pct: 80 };
  return              { niveau: 'Fort',    couleur: '#16A34A', pct: 100 };
}

// ── Wrapper visuel commun ──────────────────────────────────────────────────
function AuthCard({ children }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
      backgroundImage: `url(${bgImage})`,
      backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
      padding: '0 220px',
    }}>
      <div style={{
        background: 'white', borderRadius: 16, padding: '40px 36px',
        width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src={logo} alt="NANCOZI" style={{
            width: 80, height: 80, objectFit: 'contain', borderRadius: 14,
            margin: '0 auto 12px', display: 'block',
            boxShadow: '0 4px 16px rgba(31,78,121,0.2)',
          }} />
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1F4E79', margin: 0 }}>CRM-NANCOZI</h1>
          <p style={{ color: '#6B7280', fontSize: 12, marginTop: 4 }}>NANCOZI CORPORATION</p>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Étape 1 : demander le lien par email ───────────────────────────────────
function EtapeEmail({ dispatch }) {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [loading, setLoading]   = useState(false);
  const [envoye, setEnvoye]     = useState(false);
  const [erreur, setErreur]     = useState(null);

  const onSubmit = async ({ email }) => {
    setLoading(true);
    setErreur(null);
    try {
      await dispatch(demanderResetPassword(email)).unwrap();
      setEnvoye(true);
    } catch (e) {
      setErreur(typeof e === 'string' ? e : 'Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  if (envoye) return (
    <div style={{ textAlign: 'center' }}>
      <CheckCircle size={52} style={{ color: '#16A34A', margin: '0 auto 16px', display: 'block' }} />
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1F4E79', marginBottom: 10 }}>Email envoyé !</h2>
      <p style={{ color: '#4B5563', fontSize: 14, lineHeight: 1.6, marginBottom: 12 }}>
        Si cet email est associé à un compte CRM-NANCOZI, un lien de réinitialisation vous a été envoyé.
        Vérifiez votre boîte de réception (et les spams).
      </p>
      <p style={{ color: '#9CA3AF', fontSize: 12, marginBottom: 24 }}>
        Le lien expire dans <strong>1 heure</strong>.
      </p>
      <Link to="/login" style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        color: '#2E75B6', fontSize: 13, textDecoration: 'none', fontWeight: 500,
      }}>
        <ArrowLeft size={14} /> Retour à la connexion
      </Link>
    </div>
  );

  return (
    <>
      <div style={{ marginBottom: 22 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1F4E79', margin: '0 0 6px' }}>
          Mot de passe oublié ?
        </h2>
        <p style={{ color: '#6B7280', fontSize: 13, margin: 0 }}>
          Saisissez votre adresse email. Nous vous enverrons un lien de réinitialisation.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label className="form-label">Adresse email</label>
          <div style={{ position: 'relative' }}>
            <Mail size={16} style={{
              position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)', color: '#9CA3AF',
            }} />
            <input
              {...register('email', {
                required: "L'email est obligatoire",
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email invalide' },
              })}
              type="email"
              placeholder="votre@email.com"
              className={`form-control ${errors.email ? 'error' : ''}`}
              style={{ paddingLeft: 38 }}
              autoComplete="email"
              autoFocus
            />
          </div>
          {errors.email && <p className="form-error">{errors.email.message}</p>}
        </div>

        <button
          type="submit"
          className="header-btn btn-primary w-full"
          disabled={loading}
          style={{ justifyContent: 'center', padding: '11px 20px', fontSize: 14, marginTop: 4, width: '100%' }}
        >
          {loading
            ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Envoi en cours...</>
            : 'Envoyer le lien de réinitialisation'
          }
        </button>

        {erreur && (
          <div style={{
            marginTop: 14, padding: '10px 14px',
            background: '#FCE4EC', border: '1px solid #C00000',
            borderRadius: 8, color: '#C00000', fontSize: 13, textAlign: 'center',
          }}>
            {erreur}
          </div>
        )}
      </form>

      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Link to="/login" style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: 13, color: '#6B7280', textDecoration: 'none',
        }}>
          <ArrowLeft size={13} /> Retour à la connexion
        </Link>
      </div>
    </>
  );
}

// ── Étape 2 : saisir le nouveau mot de passe (token dans l'URL) ───────────
function EtapeNouveauMdp({ dispatch, token }) {
  const navigate = useNavigate();
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [loading, setLoading]         = useState(false);
  const [erreur, setErreur]           = useState(null);
  const [succes, setSucces]           = useState(false);
  const [showPwd, setShowPwd]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const password = watch('password', '');
  const force    = forceMotDePasse(password);

  const onSubmit = async ({ password }) => {
    setLoading(true);
    setErreur(null);
    try {
      await dispatch(confirmerResetPassword({ token, password })).unwrap();
      setSucces(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (e) {
      setErreur(typeof e === 'string' ? e : 'Token invalide ou expiré. Refaites une demande.');
    } finally {
      setLoading(false);
    }
  };

  if (succes) return (
    <div style={{ textAlign: 'center' }}>
      <CheckCircle size={52} style={{ color: '#16A34A', margin: '0 auto 16px', display: 'block' }} />
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1F4E79', marginBottom: 10 }}>
        Mot de passe modifié !
      </h2>
      <p style={{ color: '#4B5563', fontSize: 14, marginBottom: 20 }}>
        Votre mot de passe a été mis à jour. Redirection vers la connexion…
      </p>
      <Loader size={20} style={{
        color: '#2E75B6', animation: 'spin 1s linear infinite',
        margin: '0 auto', display: 'block',
      }} />
    </div>
  );

  return (
    <>
      <div style={{ marginBottom: 22 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1F4E79', margin: '0 0 6px' }}>
          Nouveau mot de passe
        </h2>
        <p style={{ color: '#6B7280', fontSize: 13, margin: 0 }}>
          Choisissez un mot de passe sécurisé (minimum 8 caractères).
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Nouveau mot de passe */}
        <div className="form-group">
          <label className="form-label">Nouveau mot de passe</label>
          <div style={{ position: 'relative' }}>
            <Lock size={16} style={{
              position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)', color: '#9CA3AF', pointerEvents: 'none',
            }} />
            <input
              {...register('password', {
                required: 'Le mot de passe est obligatoire',
                minLength: { value: 8, message: 'Minimum 8 caractères' },
              })}
              type={showPwd ? 'text' : 'password'}
              placeholder="••••••••"
              className={`form-control ${errors.password ? 'error' : ''}`}
              style={{ paddingLeft: 38, paddingRight: 42 }}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPwd(v => !v)}
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2,
              }}
            >
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="form-error">{errors.password.message}</p>}

          {/* Indicateur de force */}
          {password && (
            <div style={{ marginTop: 6 }}>
              <div style={{ height: 4, borderRadius: 2, background: '#E5E7EB', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${force.pct}%`, background: force.couleur,
                  borderRadius: 2, transition: 'width 0.3s, background 0.3s',
                }} />
              </div>
              <p style={{ fontSize: 11, color: force.couleur, margin: '3px 0 0', fontWeight: 500 }}>
                Force : {force.niveau}
              </p>
            </div>
          )}
        </div>

        {/* Confirmer mot de passe */}
        <div className="form-group">
          <label className="form-label">Confirmer le mot de passe</label>
          <div style={{ position: 'relative' }}>
            <Lock size={16} style={{
              position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)', color: '#9CA3AF', pointerEvents: 'none',
            }} />
            <input
              {...register('confirm_password', {
                required: 'Confirmez le mot de passe',
                validate: v => v === password || 'Les mots de passe ne correspondent pas',
              })}
              type={showConfirm ? 'text' : 'password'}
              placeholder="••••••••"
              className={`form-control ${errors.confirm_password ? 'error' : ''}`}
              style={{ paddingLeft: 38, paddingRight: 42 }}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(v => !v)}
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2,
              }}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirm_password && <p className="form-error">{errors.confirm_password.message}</p>}
        </div>

        <button
          type="submit"
          className="header-btn btn-primary w-full"
          disabled={loading}
          style={{ justifyContent: 'center', padding: '11px 20px', fontSize: 14, marginTop: 4, width: '100%' }}
        >
          {loading
            ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Enregistrement...</>
            : 'Enregistrer le nouveau mot de passe'
          }
        </button>

        {erreur && (
          <div style={{
            marginTop: 14, padding: '10px 14px',
            background: '#FCE4EC', border: '1px solid #C00000',
            borderRadius: 8, color: '#C00000', fontSize: 13, textAlign: 'center',
          }}>
            {erreur}
          </div>
        )}
      </form>

      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Link to="/login" style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: 13, color: '#6B7280', textDecoration: 'none',
        }}>
          <ArrowLeft size={13} /> Retour à la connexion
        </Link>
      </div>
    </>
  );
}

// ── Page principale ────────────────────────────────────────────────────────
export default function ResetPasswordPage() {
  const dispatch    = useDispatch();
  const { token }   = useParams();

  return (
    <AuthCard>
      {token
        ? <EtapeNouveauMdp dispatch={dispatch} token={token} />
        : <EtapeEmail      dispatch={dispatch} />
      }
    </AuthCard>
  );
}
